import React, { useState, useEffect, useCallback } from 'react';
// Using native HTML5 drag and drop for better performance
import {
	Box,
	Typography,
	Paper,
	Grid,
	Card,
	CardContent,
	Button,
	Chip,
	IconButton,
	Alert,
	CircularProgress,
	Avatar,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	InputAdornment,
	Tooltip,
	Badge,
	Tabs,
	Tab,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	useTheme,
	alpha,
	Autocomplete,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow
} from '@mui/material';
import {
	Add as AddIcon,
	Person as PersonIcon,
	BugReport as BugIcon,
	Assignment as TaskIcon,
	Star as StoryIcon,
	Speed as SprintIcon,
	Refresh as RefreshIcon,
	ViewList as ListViewIcon,
	TableChart as TableViewIcon,
	ViewModule as ViewModuleIcon,
	Search as SearchIcon,
	FilterList as FilterIcon,
	Edit as EditIcon,
	Delete as DeleteIcon,
	ExpandMore as ExpandMoreIcon,
	CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import Layout from '../components/Layout/Layout';
import IssueDetailsModal from '../components/Common/IssueDetailsModal';
import CreateIssueModal from '../components/Common/CreateIssueModal';
import apiService from '../services/api';
import { Issue, Project, Sprint, Epic, User, CreateIssueData, IssueType, Priority, BoardColumn } from '../types';

interface PlanningStory extends Issue {
	epic?: Epic;
	assignee?: User;
	project?: Project;
}

interface ProjectWithPlanningData extends Project {
	unassignedStories: PlanningStory[];
	sprints: Sprint[];
	totalStories: number;
}

const Planning: React.FC = () => {
	const theme = useTheme();
	const [projectsWithPlanningData, setProjectsWithPlanningData] = useState<ProjectWithPlanningData[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [dragLoading, setDragLoading] = useState(false);
	const [draggedStory, setDraggedStory] = useState<PlanningStory | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
	const [modalOpen, setModalOpen] = useState(false);

	// View state
	const [viewMode, setViewMode] = useState<'list' | 'table' | 'cards'>('list');

	// Create Issue Modal State
	const [openCreateIssueModal, setOpenCreateIssueModal] = useState(false);
	const [selectedProjectForCreation, setSelectedProjectForCreation] = useState<Project | null>(null);

	// Filtering and Search State
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedProject, setSelectedProject] = useState<ProjectWithPlanningData | null>(null);
	const [filterType, setFilterType] = useState<string>('all');
	const [filterPriority, setFilterPriority] = useState<string>('all');
	const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());

	// Import Issues State
	const [importDialogOpen, setImportDialogOpen] = useState(false);
	const [importProvider, setImportProvider] = useState<string>('');
	const [importing, setImporting] = useState(false);

	useEffect(() => {
		loadAllPlanningData();
	}, []);

	const updateLocalStateAfterDragDrop = (storyId: string, targetSprintId: string | null, projectId: string) => {
		setProjectsWithPlanningData(prevData => {
			return prevData.map(project => {
				if (project.id !== projectId) return project;

				// Find the story being moved
				let movedStory: PlanningStory | null = null;

				// Look in unassigned stories
				const updatedUnassignedStories = project.unassignedStories.filter(story => {
					if (story.id === storyId) {
						movedStory = story;
						return false; // Remove from unassigned
					}
					return true;
				});

				// Look in sprint stories
				const updatedSprints = project.sprints.map(sprint => {
					if (sprint.issues) {
						const updatedIssues = sprint.issues.filter(issue => {
							if (issue.id === storyId) {
								movedStory = issue;
								return false; // Remove from sprint
							}
							return true;
						});
						return { ...sprint, issues: updatedIssues };
					}
					return sprint;
				});

				// Add story to the target location
				if (movedStory) {
					if (targetSprintId === null) {
						// Moving to unassigned
						const updatedStory = Object.assign({}, movedStory, { sprintId: null }) as PlanningStory;
						updatedUnassignedStories.push(updatedStory);
					} else {
						// Moving to a sprint
						const updatedStory = Object.assign({}, movedStory, { sprintId: targetSprintId }) as PlanningStory;
						const targetSprint = updatedSprints.find(sprint => sprint.id === targetSprintId);
						if (targetSprint) {
							targetSprint.issues = [...(targetSprint.issues || []), updatedStory];
						}
					}
				}

				return {
					...project,
					unassignedStories: updatedUnassignedStories,
					sprints: updatedSprints
				};
			});
		});
	};

	const loadAllPlanningData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);
			
			// Load all projects
			const projects = await apiService.getProjects();
			
			// Load planning data for each project
			const projectsWithPlanningDataArray: ProjectWithPlanningData[] = [];
			
			for (const project of projects) {
				try {
					// Load unassigned issues (stories without sprint)
					const issuesResponse = await apiService.getIssues({ projectId: project.id });
					const issuesData = Array.isArray(issuesResponse.data) ? issuesResponse.data.flat() : [];
					const allIssues = issuesData.map(issue => ({
						...issue,
						project
					})) as PlanningStory[];
					
					// Filter to only show unassigned stories (those without sprintId)
					const unassignedStories = allIssues.filter(story => !story.sprintId);
					// Debug logging removed for better performance
					
					// Load sprints for the project
					const sprintsData = await apiService.getProjectSprints(project.id, false);
					
					projectsWithPlanningDataArray.push({
						...project,
						unassignedStories,
						sprints: sprintsData,
						totalStories: unassignedStories.length
					});
				} catch (err) {
					console.error(`Failed to load planning data for project ${project.name}:`, err);
					projectsWithPlanningDataArray.push({
						...project,
						unassignedStories: [],
						sprints: [],
						totalStories: 0
					});
				}
			}
			
			setProjectsWithPlanningData(projectsWithPlanningDataArray);
			
			// Expand first project by default if any projects have stories
			const firstProjectWithStories = projectsWithPlanningDataArray.find(p => p.totalStories > 0);
			if (firstProjectWithStories) {
				setExpandedProjects(new Set([firstProjectWithStories.id]));
			}
			
		} catch (err: any) {
			setError(err.response?.data?.message || 'Failed to load planning data');
		} finally {
			setLoading(false);
		}
	}, []);

	const handleProjectToggle = (projectId: string) => {
		const newExpanded = new Set(expandedProjects);
		if (newExpanded.has(projectId)) {
			newExpanded.delete(projectId);
		} else {
			newExpanded.add(projectId);
		}
		setExpandedProjects(newExpanded);
	};

	// Native HTML5 drag and drop handlers
	const handleDragStart = (e: React.DragEvent, story: PlanningStory) => {
		setIsDragging(true);
		setDraggedStory(story);
		e.dataTransfer.effectAllowed = 'move';
		e.dataTransfer.setData('text/html', e.currentTarget.outerHTML);
		
		// Add smooth drag feedback
		const target = e.currentTarget as HTMLElement;
		target.style.opacity = '0.5';
		target.style.transform = 'rotate(2deg) scale(1.02)';
		target.style.transition = 'all 0.2s ease';
	};

	const handleDragEnd = (e: React.DragEvent) => {
		// Reset drag styles
		const target = e.currentTarget as HTMLElement;
		target.style.opacity = '1';
		target.style.transform = 'rotate(0deg) scale(1)';
		target.style.transition = 'all 0.2s ease';
		setDraggedStory(null);
		setIsDragging(false);
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = 'move';
		// Visual feedback applied
		
		// Add visual feedback for drop zones
		const target = e.currentTarget as HTMLElement;
		target.style.border = `2px dashed ${theme.palette.primary.main}`;
		target.style.backgroundColor = alpha(theme.palette.primary.main, 0.1);
		target.style.transform = 'scale(1.01)';
		target.style.transition = 'all 0.2s ease';
	};

	const handleDragLeave = (e: React.DragEvent) => {
		// Reset drop zone styles
		const target = e.currentTarget as HTMLElement;
		target.style.border = '2px dashed transparent';
		target.style.backgroundColor = '';
		target.style.transform = '';
	};

	const handleDrop = async (e: React.DragEvent, targetSprintId: string | null, projectId: string) => {
		e.preventDefault();
		// Drop event processed
		
		if (!draggedStory) return;

		try {
			setDragLoading(true);
			
			if (targetSprintId) {
				// Moving to a sprint
				// Moving to sprint
				await apiService.addIssueToSprint(targetSprintId, draggedStory.id);
			} else {
				// Moving to unassigned - need to remove from current sprint
				// Moving to unassigned
				// Find the story in any sprint within the project
				const projectData = projectsWithPlanningData.find(p => p.id === projectId);
				if (projectData) {
					// Check sprints for the story
					// Check if the story is in any sprint
					for (const sprint of projectData.sprints) {
						const storyInSprint = sprint.issues?.find(issue => issue.id === draggedStory.id);
						if (storyInSprint) {
							await apiService.removeIssueFromSprint(sprint.id, draggedStory.id);
							break;
						}
					}
				}
			}
			
			// Update local state instead of reloading all data to prevent flickering
			updateLocalStateAfterDragDrop(draggedStory.id, targetSprintId, projectId);
		} catch (err: any) {
			console.error('Drag and drop error:', err);
			setError(err.response?.data?.message || 'Failed to assign story to sprint');
		} finally {
			setDragLoading(false);
		}
	};

	const handleCreateIssue = (project: Project) => {
		setSelectedProjectForCreation(project);
		setOpenCreateIssueModal(true);
	};

	const handleCloseCreateIssueModal = () => {
		setOpenCreateIssueModal(false);
		setSelectedProjectForCreation(null);
	};

	const getIssueTypeIcon = (type: string) => {
		switch (type.toLowerCase()) {
			case 'bug': return <BugIcon />;
			case 'task': return <TaskIcon />;
			case 'story': return <StoryIcon />;
			default: return <TaskIcon />;
		}
	};

	const getIssueTypeColor = (type: string) => {
		switch (type.toLowerCase()) {
			case 'bug': return 'error';
			case 'task': return 'primary';
			case 'story': return 'success';
			default: return 'default';
		}
	};

	const getPriorityColor = (priority: string) => {
		switch (priority.toLowerCase()) {
			case 'critical': return 'error';
			case 'high': return 'warning';
			case 'medium': return 'primary';
			case 'low': return 'default';
			default: return 'default';
		}
	};

	// Filter projects based on selected project
	const getFilteredProjects = (projects: ProjectWithPlanningData[]) => {
		if (!selectedProject) {
			return projects;
		}
		return projects.filter(project => project.id === selectedProject.id);
	};

	// Filter stories based on search and filter criteria
	const getFilteredStories = (stories: PlanningStory[]) => {
		return stories.filter(story => {
			// Search filter
			const matchesSearch = !searchTerm || 
				story.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
				story.issueKey?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				story.description?.toLowerCase().includes(searchTerm.toLowerCase());

			// Type filter
			const matchesType = filterType === 'all' || story.type.toLowerCase() === filterType.toLowerCase();

			// Priority filter
			const matchesPriority = filterPriority === 'all' || story.priority?.toLowerCase() === filterPriority.toLowerCase();

			return matchesSearch && matchesType && matchesPriority;
		});
	};

	// Calculate total filtered stories across filtered projects
	const getTotalFilteredStories = () => {
		const filteredProjects = getFilteredProjects(projectsWithPlanningData);
		return filteredProjects.reduce((total, project) => {
			return total + getFilteredStories(project.unassignedStories).length;
		}, 0);
	};

	if (loading) {
		return (
			<Layout>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
					<CircularProgress />
				</Box>
			</Layout>
		);
	}

	if (error) {
		return (
			<Layout>
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			</Layout>
		);
	}

	return (
		<Layout>
			<Box sx={{ p: 1 }}>
				{/* Modern Professional Header */}
				<Box sx={{ 
					p: 1,
					mb: 1,
					background: (theme) => theme.palette.mode === 'dark' 
						? 'rgba(15, 23, 42, 0.95)' 
						: 'rgba(255, 255, 255, 0.95)',
					backdropFilter: 'blur(20px)',
					borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
					boxShadow: (theme) => theme.palette.mode === 'dark' 
						? '0 4px 20px rgba(0, 0, 0, 0.3)' 
						: '0 4px 20px rgba(0, 0, 0, 0.1)'
				}}>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
						<Box>
							<Typography variant="h4" component="h1">Sprint Planning</Typography>
							<Typography variant="body2" color="text.secondary">
								{getTotalFilteredStories()} unassigned stories across {getFilteredProjects(projectsWithPlanningData).length} projects
							</Typography>
						</Box>
						<Box sx={{ display: 'flex', gap: 2 }}>
							<IconButton onClick={() => loadAllPlanningData()} disabled={loading} title="Refresh planning data">
								<RefreshIcon />
							</IconButton>
						</Box>
					</Box>

					{/* Filters */}
					<Paper sx={{ p: 1, mb: 1 }}>
						<Grid container spacing={2} alignItems="center">
							<Grid item xs={12} md={3}>
								<TextField
									fullWidth
									placeholder="Search stories..."
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<SearchIcon />
											</InputAdornment>
										),
									}}
								/>
							</Grid>
							<Grid item xs={12} md={3}>
								<Autocomplete
									options={projectsWithPlanningData}
									getOptionLabel={(option) => `${option.name} (${option.key})`}
									value={selectedProject}
									onChange={(event, newValue) => {
										setSelectedProject(newValue);
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											placeholder="Select project..."
											InputProps={{
												...params.InputProps,
												startAdornment: (
													<InputAdornment position="start">
														<SearchIcon />
													</InputAdornment>
												),
											}}
										/>
									)}
									renderOption={(props, option) => (
										<Box component="li" {...props}>
											<Box>
												<Typography variant="body2" fontWeight="medium">
													{option.name}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{option.key} • {option.methodology}
												</Typography>
											</Box>
										</Box>
									)}
									clearOnEscape
									noOptionsText="No projects found"
								/>
							</Grid>
							<Grid item xs={12} md={2}>
								<FormControl fullWidth>
									<InputLabel>Type</InputLabel>
									<Select value={filterType} onChange={(e) => setFilterType(e.target.value)} label="Type">
										<MenuItem value="all">All Types</MenuItem>
										<MenuItem value="story">Story</MenuItem>
										<MenuItem value="task">Task</MenuItem>
										<MenuItem value="bug">Bug</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} md={2}>
								<FormControl fullWidth>
									<InputLabel>Priority</InputLabel>
									<Select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} label="Priority">
										<MenuItem value="all">All Priorities</MenuItem>
										<MenuItem value="critical">Critical</MenuItem>
										<MenuItem value="high">High</MenuItem>
										<MenuItem value="medium">Medium</MenuItem>
										<MenuItem value="low">Low</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} md={2}>
								<Button
									variant="outlined"
									startIcon={<FilterIcon />}
									onClick={() => {
										setSearchTerm('');
										setSelectedProject(null);
										setFilterType('all');
										setFilterPriority('all');
									}}
									fullWidth
								>
									Clear Filters
								</Button>
							</Grid>
						</Grid>
					</Paper>

					{/* View Toggle Buttons */}
					<Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
						<Button
							variant="outlined"
							startIcon={<ListViewIcon />}
							onClick={() => setViewMode('list')}
							color={viewMode === 'list' ? 'primary' : 'inherit'}
						>
							Planning View
						</Button>
						<Button
							variant="outlined"
							startIcon={<TableViewIcon />}
							onClick={() => setViewMode('table')}
							color={viewMode === 'table' ? 'primary' : 'inherit'}
						>
							Table View
						</Button>
						<Button
							variant="outlined"
							startIcon={<ViewModuleIcon />}
							onClick={() => setViewMode('cards')}
							color={viewMode === 'cards' ? 'primary' : 'inherit'}
						>
							Cards View
						</Button>
					</Box>
				</Box>

				{dragLoading && (<Alert severity="info" sx={{ mb: 2 }}>Updating sprint assignment...</Alert>)}

				{/* Projects with Planning Data */}
				{projectsWithPlanningData.length === 0 ? (
					<Paper sx={{ p: 4, textAlign: 'center' }}>
						<Typography variant="h6" color="text.secondary">
							No projects found
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							Create a project first to start sprint planning.
						</Typography>
					</Paper>
				) : getFilteredProjects(projectsWithPlanningData).length === 0 ? (
					<Paper sx={{ p: 4, textAlign: 'center' }}>
						<Typography variant="h6" color="text.secondary">
							No projects match the current filters
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							Try selecting a different project or clearing the filters to see all projects.
						</Typography>
					</Paper>
				) : (
					<Box>
						{getFilteredProjects(projectsWithPlanningData).map((project) => {
							const filteredStories = getFilteredStories(project.unassignedStories);
							// For table and cards view, combine all stories from the project
							const allProjectStories = [
								...project.unassignedStories,
								...project.sprints.flatMap(sprint => sprint.issues || [])
							];
							const filteredAllStories = getFilteredStories(allProjectStories);
							const isExpanded = expandedProjects.has(project.id);
							
							return (
								<Accordion 
									key={project.id} 
									expanded={isExpanded}
									onChange={() => handleProjectToggle(project.id)}
									sx={{ mb: 2 }}
								>
									<AccordionSummary expandIcon={<ExpandMoreIcon />}>
										<Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
											<Box sx={{ flexGrow: 1 }}>
												<Typography variant="h6" component="h2">
													{project.name}
												</Typography>
												<Typography variant="body2" color="text.secondary">
													{project.description || 'No description available'} • {project.methodology}
												</Typography>
											</Box>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2 }}>
												<Badge badgeContent={filteredStories.length} color="primary">
													<Chip 
														label={`${project.sprints.length} Sprints`} 
														color="primary"
														size="small"
													/>
												</Badge>
												<Button
													size="small"
													startIcon={<CloudUploadIcon />}
													onClick={(e) => {
														e.stopPropagation();
														setSelectedProjectForCreation(project);
														setImportDialogOpen(true);
													}}
												>
													Import
												</Button>
												<Button
													size="small"
													startIcon={<AddIcon />}
													onClick={(e) => {
														e.stopPropagation();
														handleCreateIssue(project);
													}}
												>
													Add Story
												</Button>
											</Box>
										</Box>
									</AccordionSummary>
									<AccordionDetails>
										{viewMode === 'list' ? (
											/* Planning View (Kanban-style) */
											<Grid container spacing={3}>
												<Grid item xs={12} md={4}>
													<Paper sx={{ height: '60vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
														<Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
															<Typography variant="h6" gutterBottom>Unassigned Stories</Typography>
															<Typography variant="body2" color="text.secondary">{filteredStories.length} stories</Typography>
														</Box>
														<Box 
															sx={{ 
																flex: 1, 
																overflow: 'auto', 
																p: 2,
																minHeight: '200px',
																border: '2px dashed transparent',
																borderRadius: 2,
																transition: 'all 0.2s ease'
															}}
															onDragOver={(e) => {
																// Unassigned drop zone
																handleDragOver(e);
															}}
															onDragLeave={handleDragLeave}
															onDrop={(e) => {
																// Unassigned drop
																handleDrop(e, null, project.id);
															}}
														>
															{filteredStories.map((story) => (
																<Card 
																	key={story.id}
																	draggable
																	onDragStart={(e) => handleDragStart(e, story)}
																	onDragEnd={handleDragEnd}
																	sx={{ 
																		mb: 2, 
																		cursor: 'grab', 
																		userSelect: 'none', 
																		transition: 'all 0.2s ease',
																		border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
																		'&:hover': {
																			boxShadow: `0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`,
																			borderColor: alpha(theme.palette.primary.main, 0.25),
																			transform: 'translateY(-2px)',
																		}
																	}} 
																	onClick={() => {
																		if (!isDragging) {
																			setSelectedIssue(story as any); 
																			setModalOpen(true);
																		}
																	}}
																>
																	<CardContent sx={{ p: 2 }}>
																		<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
																			<Typography variant="body2" fontWeight="medium" sx={{ flex: 1 }}>{story.title}</Typography>
																			<Chip icon={getIssueTypeIcon(story.type)} label={story.type} color={getIssueTypeColor(story.type) as any} size="small" />
																		</Box>
																		{story.epic && (<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{story.epic.name}</Typography>)}
																		<Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
																			<Chip label={story.priority} color={getPriorityColor(story.priority) as any} size="small" />
																			{story.storyPoints && (<Chip label={`${story.storyPoints} SP`} size="small" />)}
																		</Box>
																		{story.assignee && (
																			<Box sx={{ display: 'flex', alignItems: 'center' }}>
																				<Avatar sx={{ width: 20, height: 20, mr: 1 }}><PersonIcon /></Avatar>
																				<Typography variant="caption">{story.assignee.firstName} {story.assignee.lastName}</Typography>
																			</Box>
																		)}
																	</CardContent>
																</Card>
															))}
														</Box>
													</Paper>
												</Grid>
													{project.sprints.map((sprint) => (
														<Grid item xs={12} md={4} key={sprint.id}>
															<Paper sx={{ height: '60vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
																<Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
																	<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
																		<Typography variant="h6">{sprint.name}</Typography>
																		<Chip icon={<SprintIcon />} label={sprint.state} color={sprint.state === 'active' ? 'success' : 'default'} size="small" />
																	</Box>
																	<Typography variant="body2" color="text.secondary" gutterBottom>{sprint.goal}</Typography>
																	<Typography variant="caption" color="text.secondary">{sprint.issues?.length || 0} stories • {sprint.plannedStoryPoints || 0} SP</Typography>
																</Box>
																<Box 
																	sx={{ 
																		flex: 1, 
																		overflow: 'auto', 
																		p: 2,
																		minHeight: '200px',
																		border: '2px dashed transparent',
																		borderRadius: 2,
																		transition: 'all 0.2s ease'
																	}}
																	onDragOver={(e) => {
																		console.log(`Sprint ${sprint.name} drop zone - drag over`);
																		handleDragOver(e);
																	}}
																	onDragLeave={handleDragLeave}
																	onDrop={(e) => {
																		console.log(`Sprint ${sprint.name} drop zone - drop event`);
																		handleDrop(e, sprint.id, project.id);
																	}}
																>
																	{sprint.issues?.map((story) => (
																		<Card 
																			key={story.id}
																			draggable
																			onDragStart={(e) => handleDragStart(e, story)}
																			onDragEnd={handleDragEnd}
																			sx={{ 
																				mb: 2, 
																				cursor: 'grab', 
																				userSelect: 'none', 
																				transition: 'all 0.2s ease',
																				border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
																				'&:hover': {
																					boxShadow: `0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`,
																					borderColor: alpha(theme.palette.primary.main, 0.25),
																					transform: 'translateY(-2px)',
																				}
																			}} 
																			onClick={() => {
																				if (!isDragging) {
																					setSelectedIssue(story as any); 
																					setModalOpen(true);
																				}
																			}}
																		>
																			<CardContent sx={{ p: 2 }}>
																				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
																					<Typography variant="body2" fontWeight="medium" sx={{ flex: 1 }}>{story.title}</Typography>
																					<Chip icon={getIssueTypeIcon(story.type)} label={story.type} color={getIssueTypeColor(story.type) as any} size="small" />
																				</Box>
																				{story.epic && (<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{story.epic.name}</Typography>)}
																				<Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
																					<Chip label={story.priority} color={getPriorityColor(story.priority) as any} size="small" />
																					{story.storyPoints && (<Chip label={`${story.storyPoints} SP`} size="small" />)}
																				</Box>
																				{story.assignee && (
																					<Box sx={{ display: 'flex', alignItems: 'center' }}>
																						<Avatar sx={{ width: 20, height: 20, mr: 1 }}><PersonIcon /></Avatar>
																						<Typography variant="caption">{story.assignee.firstName} {story.assignee.lastName}</Typography>
																					</Box>
																				)}
																			</CardContent>
																		</Card>
																	))}
																</Box>
															</Paper>
														</Grid>
													))}
												</Grid>
										) : viewMode === 'table' ? (
											/* Table View for this project */
											<Paper sx={{ width: '100%', overflow: 'hidden' }}>
												<Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
													<Typography variant="h6">{project.name} - All Stories</Typography>
													<Typography variant="body2" color="text.secondary">
														{filteredAllStories.length} stories • {project.sprints.length} sprints
													</Typography>
												</Box>
												{filteredAllStories.length === 0 ? (
													<Box sx={{ textAlign: 'center', py: 4 }}>
														<Typography variant="body2" color="text.secondary">
															No stories match the current filters
														</Typography>
													</Box>
												) : (
													<TableContainer>
														<Table>
															<TableHead>
																<TableRow>
																	<TableCell>Story</TableCell>
																	<TableCell>Type</TableCell>
																	<TableCell>Sprint</TableCell>
																	<TableCell>Priority</TableCell>
																	<TableCell>Story Points</TableCell>
																	<TableCell>Assignee</TableCell>
																	<TableCell>Epic</TableCell>
																</TableRow>
															</TableHead>
															<TableBody>
																{filteredAllStories.map((story) => {
																	// Find which sprint this story belongs to
																	const storySprint = project.sprints.find(sprint => 
																		sprint.issues?.some(issue => issue.id === story.id)
																	);
																	
																	return (
																		<TableRow 
																			key={story.id} 
																			hover 
																			sx={{ cursor: 'pointer' }}
																			onClick={() => { setSelectedIssue(story as any); setModalOpen(true); }}
																		>
																			<TableCell>
																				<Box>
																					<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
																						{story.title}
																					</Typography>
																					{story.description && (
																						<Typography variant="body2" color="text.secondary" sx={{ 
																							overflow: 'hidden',
																							textOverflow: 'ellipsis',
																							whiteSpace: 'nowrap',
																							maxWidth: '300px'
																						}}>
																							{story.description}
																						</Typography>
																					)}
																				</Box>
																			</TableCell>
																			<TableCell>
																				<Chip 
																					icon={getIssueTypeIcon(story.type)} 
																					label={story.type} 
																					color={getIssueTypeColor(story.type) as any} 
																					size="small" 
																				/>
																			</TableCell>
																			<TableCell>
																				{storySprint ? (
																					<Chip 
																						icon={<SprintIcon />}
																						label={storySprint.name}
																						size="small"
																						variant="outlined"
																						color={storySprint.state === 'active' ? 'success' : 'default'}
																					/>
																				) : (
																					<Chip 
																						label="Unassigned"
																						size="small"
																						variant="outlined"
																						color="default"
																					/>
																				)}
																			</TableCell>
																			<TableCell>
																				<Chip 
																					label={story.priority} 
																					color={getPriorityColor(story.priority) as any} 
																					size="small" 
																				/>
																			</TableCell>
																			<TableCell>
																				{story.storyPoints ? (
																					<Chip 
																						label={`${story.storyPoints} SP`} 
																						size="small" 
																						variant="outlined" 
																					/>
																				) : (
																					<Typography variant="body2" color="text.secondary">
																						-
																					</Typography>
																				)}
																			</TableCell>
																			<TableCell>
																				{story.assignee ? (
																					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
																						<Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
																							{story.assignee.firstName?.[0]}{story.assignee.lastName?.[0]}
																						</Avatar>
																						<Typography variant="body2">
																							{story.assignee.firstName} {story.assignee.lastName}
																						</Typography>
																					</Box>
																				) : (
																					<Typography variant="body2" color="text.secondary">
																						Unassigned
																					</Typography>
																				)}
																			</TableCell>
																			<TableCell>
																				{story.epic ? (
																					<Typography variant="body2">
																						{story.epic.name}
																					</Typography>
																				) : (
																					<Typography variant="body2" color="text.secondary">
																						-
																					</Typography>
																				)}
																			</TableCell>
																		</TableRow>
																	);
																})}
															</TableBody>
														</Table>
													</TableContainer>
												)}
											</Paper>
										) : (
											/* Cards View for this project */
											<Paper sx={{ width: '100%', overflow: 'hidden' }}>
												<Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
													<Typography variant="h6">{project.name} - All Stories</Typography>
													<Typography variant="body2" color="text.secondary">
														{filteredAllStories.length} stories • {project.sprints.length} sprints
													</Typography>
												</Box>
												<Box sx={{ p: 2 }}>
													{filteredAllStories.length === 0 ? (
														<Box sx={{ textAlign: 'center', py: 4 }}>
															<Typography variant="body2" color="text.secondary">
																No stories match the current filters
															</Typography>
														</Box>
													) : (
														<Grid container spacing={3}>
															{filteredAllStories.map((story) => {
																// Find which sprint this story belongs to
																const storySprint = project.sprints.find(sprint => 
																	sprint.issues?.some(issue => issue.id === story.id)
																);
																
																return (
																	<Grid item xs={12} sm={6} md={4} lg={3} key={story.id}>
																		<Card 
																			sx={{ 
																				height: '100%',
																				display: 'flex',
																				flexDirection: 'column',
																				cursor: 'pointer',
																				transition: 'all 0.2s ease',
																				'&:hover': { 
																					boxShadow: 4,
																					transform: 'translateY(-2px)'
																				}
																			}}
																			onClick={() => { setSelectedIssue(story as any); setModalOpen(true); }}
																		>
																			<CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
																				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
																					<Typography variant="subtitle2" fontWeight="600" sx={{ flex: 1, mr: 1 }}>
																						{story.title}
																					</Typography>
																					<Chip 
																						icon={getIssueTypeIcon(story.type)} 
																						label={story.type} 
																						color={getIssueTypeColor(story.type) as any} 
																						size="small" 
																					/>
																				</Box>
																				
																				{story.epic && (
																					<Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
																						{story.epic.name}
																					</Typography>
																				)}
																				
																				{storySprint && (
																					<Box sx={{ mb: 1 }}>
																						<Chip 
																							icon={<SprintIcon />}
																							label={storySprint.name}
																							size="small"
																							variant="outlined"
																							color={storySprint.state === 'active' ? 'success' : 'default'}
																						/>
																					</Box>
																				)}
																				
																				<Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
																					<Chip 
																						label={story.priority} 
																						color={getPriorityColor(story.priority) as any} 
																						size="small" 
																					/>
																					{story.storyPoints && (
																						<Chip 
																							label={`${story.storyPoints} SP`} 
																							size="small" 
																							variant="outlined" 
																						/>
																					)}
																				</Box>
																				
																				{story.assignee && (
																					<Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
																						<Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: '0.75rem' }}>
																							{story.assignee.firstName?.[0]}{story.assignee.lastName?.[0]}
																						</Avatar>
																						<Typography variant="caption" color="text.secondary">
																							{story.assignee.firstName} {story.assignee.lastName}
																						</Typography>
																					</Box>
																				)}
																			</CardContent>
																		</Card>
																	</Grid>
																);
															})}
														</Grid>
													)}
												</Box>
											</Paper>
										)}
									</AccordionDetails>
								</Accordion>
							);
						})}
					</Box>
				)}
			</Box>

			{/* Create Issue Modal */}
			{selectedProjectForCreation && (
				<CreateIssueModal
					open={openCreateIssueModal}
					onClose={handleCloseCreateIssueModal}
					projectId={selectedProjectForCreation.id}
					defaultValues={{ type: IssueType.STORY, priority: Priority.MEDIUM }}
					onCreated={async () => { 
						await loadAllPlanningData();
						handleCloseCreateIssueModal();
					}}
				/>
			)}

			{/* Import Issues Dialog */}
			<Dialog open={importDialogOpen} onClose={() => {
				setImportDialogOpen(false);
				setSelectedProjectForCreation(null);
			}} maxWidth="sm" fullWidth>
				<DialogTitle>
					Import Issues
					{selectedProjectForCreation && (
						<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							Importing into: <strong>{selectedProjectForCreation.name}</strong>
						</Typography>
					)}
				</DialogTitle>
				<DialogContent>
					<Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
						<FormControl fullWidth>
							<InputLabel>Source</InputLabel>
							<Select
								value={importProvider}
								label="Source"
								onChange={(e) => setImportProvider(e.target.value as any)}
							>
								<MenuItem value="jira_excel">Jira — Excel/CSV</MenuItem>
								<MenuItem value="ms_planner_excel">Microsoft Planner — Excel</MenuItem>
								<MenuItem value="clickup_excel">ClickUp — Excel/CSV</MenuItem>
								<MenuItem value="trello_excel">Trello — Excel/CSV</MenuItem>
							</Select>
						</FormControl>
						<Typography variant="body2" color="text.secondary">
							Select the source platform and upload your Excel/CSV file to import issues into this project.
						</Typography>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => {
						setImportDialogOpen(false);
						setSelectedProjectForCreation(null);
					}} disabled={importing}>Close</Button>
					<Button 
						variant="contained" 
						disabled={!importProvider || importing}
						onClick={() => {
							// TODO: Implement actual import functionality
							console.log('Import issues for project:', selectedProjectForCreation?.id, 'from:', importProvider);
						}}
					>
						{importing ? 'Importing...' : 'Import Issues'}
					</Button>
				</DialogActions>
			</Dialog>

			<IssueDetailsModal
				open={modalOpen}
				onClose={() => { setModalOpen(false); setSelectedIssue(null); }}
				issue={selectedIssue as any}
				onUpdate={async () => { await loadAllPlanningData(); }}
				onOpenIssue={(issue) => { setSelectedIssue(issue); setModalOpen(true); }}
				boardColumns={[]}
			/>
		</Layout>
	);
};

export default Planning;